/* The three field types that hold something richer than a line of text.
 *
 * `Text Editor`    -- an invoice's terms, a job description: formatted prose.
 * `HTML Editor`    -- a print format's markup: code, edited as code.
 * `Markdown Editor`-- notes and documentation, with a preview.
 *
 * All three were a textarea or a bare contentEditable before, which is why
 * they were marked partial: you could type into them and not much else. The
 * acceptance for this is small and exact -- somebody can bold a line in an
 * invoice's terms and it survives a save and a print -- and everything here
 * serves that.
 *
 * `document.execCommand` is deprecated and has no replacement that works in
 * every browser today; the alternative is a large editor dependency for a
 * field most people use to write two sentences. It is used only for the
 * formatting commands, and everything it produces is sanitised on the way
 * out, so what it does in the DOM is never what gets stored.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { Control } from './kit'
import { Field, asText } from './kit'
import { sanitiseHtml } from '../sanitise'

type Command = {
  /** What execCommand calls it. */
  name: string
  value?: string
  /** What it is called on screen and to a screen reader. */
  label: string
  /** What is drawn in the button. */
  glyph: string
  /** The keyboard shortcut, if it has one. */
  key?: string
}

const COMMANDS: (Command | 'gap')[] = [
  { name: 'bold', label: 'Bold', glyph: 'B', key: 'b' },
  { name: 'italic', label: 'Italic', glyph: 'I', key: 'i' },
  { name: 'underline', label: 'Underline', glyph: 'U', key: 'u' },
  'gap',
  { name: 'formatBlock', value: 'h3', label: 'Heading', glyph: 'H' },
  { name: 'insertUnorderedList', label: 'Bulleted list', glyph: '•' },
  { name: 'insertOrderedList', label: 'Numbered list', glyph: '1.' },
  'gap',
  { name: 'createLink', label: 'Link', glyph: '🔗' },
  { name: 'removeFormat', label: 'Clear formatting', glyph: '⌫' },
]

export const RichTextControl: Control = (p) => {
  const box = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<Record<string, boolean>>({})
  const label = p.field.label || p.field.fieldname
  const id = useId()

  // Only write into the box when the value came from somewhere else. Writing
  // on every keystroke moves the caret to the start, which makes the field
  // impossible to type in -- the classic contentEditable bug.
  useEffect(() => {
    const wanted = sanitiseHtml(asText(p.value))
    if (box.current && box.current.innerHTML !== wanted) box.current.innerHTML = wanted
  }, [p.value])

  const publish = useCallback(() => {
    if (box.current) p.onChange(sanitiseHtml(box.current.innerHTML))
  }, [p])

  /** Which buttons should look pressed, for where the caret is now. */
  const readState = useCallback(() => {
    if (p.readOnly) return
    const next: Record<string, boolean> = {}
    for (const c of COMMANDS) {
      if (c === 'gap' || c.name === 'createLink' || c.name === 'removeFormat') continue
      try { next[c.name] = document.queryCommandState(c.name) } catch { /* not supported */ }
    }
    setActive(next)
  }, [p.readOnly])

  const run = (command: Command) => {
    if (p.readOnly) return
    box.current?.focus()
    if (command.name === 'createLink') {
      const url = window.prompt('Link to where?')
      if (!url) return
      // Sanitised on the way out anyway; refused here too so the person is
      // told, rather than silently losing the link they just typed.
      if (!/^(https?:|mailto:|tel:|\/)/i.test(url.trim())) {
        window.alert('Links can point to a web address, an email address or a phone number.')
        return
      }
      document.execCommand('createLink', false, url.trim())
    } else {
      document.execCommand(command.name, false, command.value)
    }
    publish()
    readState()
  }

  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div className="rte" data-readonly={p.readOnly ? 'true' : undefined}>
        {!p.readOnly && (
          <div className="rte-bar" role="toolbar" aria-label={`Formatting for ${label}`}>
            {COMMANDS.map((c, i) => c === 'gap'
              ? <span key={`gap${i}`} className="rte-gap" aria-hidden="true" />
              : (
                <button key={c.name + (c.value ?? '')} type="button" className="rte-btn"
                        aria-label={c.label + (c.key ? ` (Ctrl+${c.key.toUpperCase()})` : '')}
                        aria-pressed={c.name in active ? active[c.name] : undefined}
                        title={c.label}
                        // The toolbar must not take focus, or the selection in
                        // the box is lost before the command can act on it.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => run(c)}>
                  <span aria-hidden="true">{c.glyph}</span>
                </button>
              ))}
          </div>
        )}
        <div
          ref={box} id={id} className="rte-box"
          contentEditable={!p.readOnly} suppressContentEditableWarning
          role="textbox" aria-multiline="true" aria-label={label}
          onInput={readState}
          onKeyUp={readState}
          onMouseUp={readState}
          onBlur={publish}
          onKeyDown={(e) => {
            const hit = COMMANDS.find((c) => c !== 'gap' && c.key === e.key.toLowerCase())
            if (hit && hit !== 'gap' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              run(hit)
            }
          }}
          onPaste={(e) => {
            // Paste is the whole reason the sanitiser exists: the browser
            // would otherwise insert the source page's markup, scripts and
            // all, straight into a document we later print and email.
            e.preventDefault()
            const html = e.clipboardData.getData('text/html')
            const text = e.clipboardData.getData('text/plain')
            if (html) document.execCommand('insertHTML', false, sanitiseHtml(html))
            else document.execCommand('insertText', false, text)
            publish()
          }}
        />
      </div>
    </Field>
  )
}

/** Markdown: written as text, read as text, with a preview a click away.
 *
 *  No markdown library. The preview renders the handful of things people
 *  actually use and leaves the rest as written, which is honest -- and every
 *  bit of it goes through the sanitiser, because markdown allows raw HTML. */
export const MarkdownControl: Control = (p) => {
  const [showing, setShowing] = useState<'write' | 'preview'>('write')
  const label = p.field.label || p.field.fieldname
  const text = asText(p.value)

  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div className="rte">
        <div className="rte-bar" role="tablist" aria-label={`${label} view`}>
          {(['write', 'preview'] as const).map((which) => (
            <button key={which} type="button" role="tab" className="rte-tab"
                    aria-selected={showing === which} data-on={showing === which || undefined}
                    onClick={() => setShowing(which)}>
              {which === 'write' ? 'Write' : 'Preview'}
            </button>
          ))}
        </div>
        {showing === 'write' ? (
          <textarea className="rte-box mono" rows={8} aria-label={label}
                    readOnly={p.readOnly} value={text}
                    onChange={(e) => p.onChange(e.target.value)} />
        ) : (
          <div className="rte-box" aria-label={`${label}, preview`}
               dangerouslySetInnerHTML={{ __html: markdownToHtml(text) }} />
        )}
      </div>
    </Field>
  )
}

/** The common half of markdown, and nothing invented. */
export function markdownToHtml(source: string): string {
  const escaped = source
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const inline = (s: string) => s
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|\W)\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  const out: string[] = []
  let list: 'ul' | 'ol' | null = null
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null } }

  for (const line of escaped.split('\n')) {
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line)
    if (heading) {
      closeList()
      const level = Math.min((heading[1] ?? '#').length + 1, 6)
      out.push(`<h${level}>${inline(heading[2] ?? '')}</h${level}>`)
    } else if (bullet) {
      if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul' }
      out.push(`<li>${inline(bullet[1] ?? '')}</li>`)
    } else if (numbered) {
      if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol' }
      out.push(`<li>${inline(numbered[1] ?? '')}</li>`)
    } else if (!line.trim()) {
      closeList()
    } else {
      closeList()
      out.push(`<p>${inline(line)}</p>`)
    }
  }
  closeList()
  // Markdown permits raw HTML, so the result is not trusted just because we
  // built most of it.
  return sanitiseHtml(out.join(''))
}

/** HTML and code, edited as code: monospace, a real Tab, and a line count.
 *
 *  Not sanitised. This field *is* the markup -- a print format's HTML -- and
 *  cleaning it would silently break the thing somebody is writing. It is only
 *  reachable by people who may edit a print format, and Frappe renders it
 *  server-side where a script tag is inert anyway.
 */
export const CodeControl: Control = (p) => {
  const label = p.field.label || p.field.fieldname
  const text = asText(p.value)
  const lines = text ? text.split('\n').length : 1

  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div className="rte">
        <textarea
          className="rte-box mono" rows={Math.min(Math.max(lines + 1, 6), 24)}
          aria-label={label} readOnly={p.readOnly} value={text} spellCheck={false}
          onChange={(e) => p.onChange(e.target.value)}
          onKeyDown={(e) => {
            // Tab indents. Leaving it to move focus makes a code field
            // impossible to indent in; Escape then Tab still leaves.
            if (e.key !== 'Tab' || e.shiftKey) return
            e.preventDefault()
            const area = e.currentTarget
            const { selectionStart: from, selectionEnd: to, value } = area
            p.onChange(`${value.slice(0, from)}  ${value.slice(to)}`)
            requestAnimationFrame(() => area.setSelectionRange(from + 2, from + 2))
          }}
        />
        <div className="rte-foot small muted">
          {lines} line{lines === 1 ? '' : 's'} · Tab indents, Escape then Tab leaves
        </div>
      </div>
    </Field>
  )
}
